import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslationReady } from '../../../hooks/useTranslationReady';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import { toast } from 'react-hot-toast';
import {
  Save,
  ArrowLeft,
  Settings,
  LayoutGrid,
  GitBranch,
  List,
  Eye,
  Plus,
  Trash2,
  Edit,
  GripVertical,
  AlertCircle,
  CheckCircle,
  Loader
} from 'lucide-react';
import { loadPipelineConfig, savePipelineConfig, clearPipelineConfigCache, getAvailableIcons } from '../../../utils/pipelineConfig';
import StageManager from './components/StageManager';
import StageMappingEditor from './components/StageMappingEditor';
import OpportunityStagesEditor from './components/OpportunityStagesEditor';
import PipelinePreview from './components/PipelinePreview';

const PipelineStudio = () => {
  const { t, ready, translationsReady, safeT } = useTranslationReady([
    'pipeline.studio.title',
    'pipeline.studio.subtitle',
    'common.save',
    'common.cancel',
    'common.loading',
    'common.error'
  ]);
  const navigate = useNavigate();
  const { getProtectedTenantCode } = useTenantProtection();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('stages');
  const [config, setConfig] = useState(null);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [availableIcons, setAvailableIcons] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadConfig();
    loadIcons();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const tenantCode = getProtectedTenantCode();
      const loadedConfig = await loadPipelineConfig(tenantCode);
      setConfig(loadedConfig);
      setOriginalConfig(JSON.parse(JSON.stringify(loadedConfig)));
      setHasChanges(false);
    } catch (error) {
      console.error('Error loading pipeline config:', error);
      toast.error('Failed to load pipeline configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadIcons = async () => {
    try {
      const icons = await getAvailableIcons();
      setAvailableIcons(icons);
    } catch (error) {
      console.error('Error loading icons:', error);
    }
  };

  const handleConfigChange = (newConfig) => {
    setConfig(newConfig);
    setHasChanges(JSON.stringify(newConfig) !== JSON.stringify(originalConfig));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const tenantCode = getProtectedTenantCode();
      const savedConfig = await savePipelineConfig(config, tenantCode);
      setConfig(savedConfig);
      setOriginalConfig(JSON.parse(JSON.stringify(savedConfig)));
      setHasChanges(false);
      clearPipelineConfigCache();
      toast.success('Pipeline configuration saved successfully');
    } catch (error) {
      console.error('Error saving pipeline config:', error);
      toast.error(error.message || 'Failed to save pipeline configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to discard all changes?')) {
      setConfig(JSON.parse(JSON.stringify(originalConfig)));
      setHasChanges(false);
      toast.success('Changes discarded');
    }
  };

  if (!translationsReady || !ready) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-4">
        Failed to load pipeline configuration
      </div>
    );
  }

  const tabs = [
    { id: 'stages', label: 'Stage Management', icon: LayoutGrid },
    { id: 'mapping', label: 'Stage Mapping', icon: GitBranch },
    { id: 'opportunityStages', label: 'Opportunity Stages', icon: List },
    { id: 'preview', label: 'Preview', icon: Eye }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg hover:bg-white/20 transition-colors"
                title="Go back"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold">
                  {safeT('pipeline.studio.title', 'Pipeline Studio')}
                </h1>
                <p className="text-indigo-100 mt-1">
                  {safeT('pipeline.studio.subtitle', 'Configure your sales pipeline stages and mappings')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {hasChanges && (
                <button
                  onClick={handleReset}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                >
                  Reset
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="px-4 py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {safeT('common.save', 'Save')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex space-x-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 border-b-2 transition-colors flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                      : 'border-transparent text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {activeTab === 'stages' && (
          <StageManager
            config={config}
            availableIcons={availableIcons}
            onChange={handleConfigChange}
          />
        )}
        {activeTab === 'mapping' && (
          <StageMappingEditor
            config={config}
            onChange={handleConfigChange}
          />
        )}
        {activeTab === 'opportunityStages' && (
          <OpportunityStagesEditor
            config={config}
            onChange={handleConfigChange}
          />
        )}
        {activeTab === 'preview' && (
          <PipelinePreview config={config} />
        )}
      </div>
    </div>
  );
};

export default PipelineStudio;

